/**
 * Active users + retention — DAU / rolling WAU / rolling MAU charts and the
 * signup-cohort grids: the last 12 weeks and the last 12 months. The grids
 * can be cut by where the signups came from and whether they have a teacher.
 * See Backend/functions/src/admin/analytics/getRetentionStats.ts.
 *
 * `CohortGrid` is shared with Teacher Stats, so both read the same way.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ComposedChart, LineChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Modal } from '@shared-ui/components';
import {
  adminApi, COHORT_DRILLDOWN_MAX,
  type RetentionSegment, type RetentionStats, type SignupSource, type TeacherLink,
} from '../../api/adminApi';

const DAU_COLOR = '#0ea5e9';
const DAU_AVG_COLOR = '#f97316';
const WAU_COLOR = '#6366f1';
const MAU_COLOR = '#f59e0b';
const DAU_WAU_COLOR = '#22c55e';
const WAU_MAU_COLOR = '#a855f7';
const DAU_MAU_COLOR = '#ef4444';

const TOOLTIP_CONTENT_STYLE = {
  background: 'var(--bg-primary)',
  border: '1px solid var(--border-color)',
  borderRadius: '8px',
  color: 'var(--text-primary)',
};

function formatDay(date: string): string {
  return new Date(date + 'T00:00:00Z').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  });
}

function formatWeekRange(date: string): string {
  const start = new Date(date + 'T00:00:00Z');
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  const s = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const e = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return `${s} – ${e}`;
}

function formatMonth(date: string): string {
  return new Date(date + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** Trailing 7-day mean of DAU, smoothing the weekday/weekend sawtooth. */
function withDauAverage(daily: RetentionStats['daily']) {
  return daily.map((d, i) => {
    const window = daily.slice(Math.max(0, i - 6), i + 1);
    const avg = window.reduce((s, w) => s + w.dau, 0) / window.length;
    return { ...d, dauAvg: Math.round(avg * 10) / 10 };
  });
}

/**
 * Stickiness ratios per day.
 *
 * Null rather than 0 when the denominator is 0: a day with no weekly actives
 * has no ratio, and plotting it as 0% would draw a crash that never happened.
 * The rolling windows are already warm at the left edge — getRetentionStats
 * fetches 27 days before the range start for exactly this reason — so these
 * are meaningful from the first plotted day.
 */
function withRatios(daily: RetentionStats['daily']) {
  const pct = (num: number, den: number) => (den > 0 ? Math.round((num / den) * 1000) / 10 : null);
  return daily.map(d => ({
    ...d,
    dauWau: pct(d.dau, d.wau),
    wauMau: pct(d.wau, d.mau),
    dauMau: pct(d.dau, d.mau),
  }));
}

export function RetentionTab({ data, isLoading, rangeLabel, includeAll }: {
  data: RetentionStats | undefined;
  isLoading: boolean;
  rangeLabel: string;
  /** Audience filter — passed through so a drilled-down cell lists the same
   *  population the number was computed from. */
  includeAll?: boolean;
}) {
  const rows = useMemo(() => (data ? withRatios(withDauAverage(data.daily)) : []), [data]);
  const [segment, setSegment] = useState<RetentionSegment>({});
  const filtered = segment.source !== undefined || segment.teacher !== undefined;
  // The grids don't follow the range, so a sliced read asks for one day of
  // the daily series — it only wants the cohorts.
  const sliced = useQuery({
    queryKey: ['retentionStats', 'segment', includeAll, segment],
    queryFn: () => adminApi.getRetentionStats(1, includeAll, segment),
    enabled: filtered,
  });
  const cohortSource = filtered ? sliced.data : data;

  if (isLoading) return <div className="loading">Loading retention…</div>;
  if (!data) return null;

  const axisProps = {
    tick: { fontSize: 11 },
    stroke: 'var(--text-tertiary, #9ca3af)',
  };

  return (
    <div className="retention-tab">
      <div className="stat-cards stat-cards--4col">
        <div className="stat-card stat-card--fact">
          <div className="stat-card-value">{data.totals.dau}</div>
          <div className="stat-card-label">DAU (today)</div>
        </div>
        <div className="stat-card stat-card--fact">
          <div className="stat-card-value">{data.totals.wau}</div>
          <div className="stat-card-label">WAU (last 7 days)</div>
        </div>
        <div className="stat-card stat-card--fact">
          <div className="stat-card-value">{data.totals.mau}</div>
          <div className="stat-card-label">MAU (last 28 days)</div>
        </div>
        <div className="stat-card stat-card--fact">
          <div className="stat-card-value">{data.totals.stickiness}%</div>
          <div className="stat-card-label">Stickiness (DAU/WAU)</div>
        </div>
      </div>

      <div className="chart-card" style={{ marginBottom: 24 }}>
        <h3>Daily Active Users ({rangeLabel})</h3>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
            <XAxis
              dataKey="date"
              tickFormatter={(v) => formatDay(String(v))}
              interval="preserveStartEnd"
              minTickGap={20}
              {...axisProps}
            />
            <YAxis allowDecimals={false} {...axisProps} />
            <Tooltip
              labelFormatter={(label) => formatDay(String(label))}
              contentStyle={TOOLTIP_CONTENT_STYLE}
              labelStyle={{ color: 'var(--text-primary)' }}
              itemStyle={{ color: 'var(--text-primary)' }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="dau" name="DAU" fill={DAU_COLOR} />
            <Line
              type="monotone"
              dataKey="dauAvg"
              name="7-day avg"
              stroke={DAU_AVG_COLOR}
              strokeWidth={2}
              strokeDasharray="4 3"
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-card" style={{ marginBottom: 24 }}>
        <h3>Rolling Active Users ({rangeLabel})</h3>
        <p className="retention-note">
          Plotted per day: WAU is the distinct users active in the trailing 7 days, MAU the trailing 28.
        </p>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
            <XAxis
              dataKey="date"
              tickFormatter={(v) => formatDay(String(v))}
              interval="preserveStartEnd"
              minTickGap={20}
              {...axisProps}
            />
            <YAxis allowDecimals={false} {...axisProps} />
            <Tooltip
              labelFormatter={(label) => formatDay(String(label))}
              contentStyle={TOOLTIP_CONTENT_STYLE}
              labelStyle={{ color: 'var(--text-primary)' }}
              itemStyle={{ color: 'var(--text-primary)' }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="wau" name="WAU (7d)" stroke={WAU_COLOR} strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="mau" name="MAU (28d)" stroke={MAU_COLOR} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-card" style={{ marginBottom: 24 }}>
        <h3>Stickiness Ratios ({rangeLabel})</h3>
        <p className="retention-note">
          How often a period's users come back within it. DAU/WAU is the headline stickiness —
          days per week an active user shows up. WAU/MAU says how much of the month's base is
          reached in a given week, and DAU/MAU is the strictest read of habit. A gap between a
          rising WAU/MAU and a flat DAU/WAU means breadth without frequency.
        </p>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
            <XAxis
              dataKey="date"
              tickFormatter={(v) => formatDay(String(v))}
              interval="preserveStartEnd"
              minTickGap={20}
              {...axisProps}
            />
            <YAxis domain={[0, 100]} unit="%" {...axisProps} />
            <Tooltip
              labelFormatter={(label) => formatDay(String(label))}
              formatter={(value) => (value == null ? '—' : `${value}%`)}
              contentStyle={TOOLTIP_CONTENT_STYLE}
              labelStyle={{ color: 'var(--text-primary)' }}
              itemStyle={{ color: 'var(--text-primary)' }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="dauWau" name="DAU/WAU" stroke={DAU_WAU_COLOR} strokeWidth={2} dot={false} connectNulls={false} />
            <Line type="monotone" dataKey="wauMau" name="WAU/MAU" stroke={WAU_MAU_COLOR} strokeWidth={2} dot={false} connectNulls={false} />
            <Line type="monotone" dataKey="dauMau" name="DAU/MAU" stroke={DAU_MAU_COLOR} strokeWidth={2} dot={false} connectNulls={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <SegmentFilter segment={segment} onChange={setSegment} />
      {filtered && sliced.isLoading && <div className="loading">Loading cohorts…</div>}
      {filtered && sliced.error && <div className="error">Could not load the cohorts for this filter.</div>}
      {cohortSource && (
        <>
          <CohortGrid
            grain="week"
            rows={cohortSource.cohorts.map(c => ({ start: c.cohortWeek, size: c.size, active: c.weeklyActive }))}
            drill={{ includeAll, segment }}
          />
          <CohortGrid
            grain="month"
            rows={(cohortSource.monthlyCohorts ?? []).map(c => ({ start: c.cohortMonth, size: c.size, active: c.monthlyActive }))}
            drill={{ includeAll, segment }}
          />
        </>
      )}
    </div>
  );
}

export type Grain = 'week' | 'month';

/** One signup cohort: when it started, how many joined, and how many were
 *  active in each period since — index 0 being the signup period. */
interface CohortRowView { start: string; size: number; active: number[] }

/** Which cell the user opened. Null when the panel is closed. */
interface OpenCell { grain: Grain; start: string; index: number; count: number; label: string }

const GRAIN = {
  week: { title: 'Weekly', unit: 'week', units: 'weeks', prefix: 'W', anchor: 'Signup week', span: 'last 12 weeks', label: formatWeekRange },
  month: { title: 'Monthly', unit: 'month', units: 'months', prefix: 'M', anchor: 'Signup month', span: 'last 12 months', label: formatMonth },
} as const;

const SOURCE_OPTIONS: { value?: SignupSource; label: string; hint?: string }[] = [
  { label: 'All sources' },
  { value: 'self', label: 'Signed up themselves', hint: 'Public accounts nobody made for them' },
  { value: 'teacher_invite', label: 'Invited by a teacher', hint: 'Public accounts a teacher made or invited' },
  { value: 'school', label: 'School accounts', hint: 'Accounts in a school tenant' },
  { value: 'admin', label: 'Added by admin', hint: 'Public accounts an admin made or invited' },
];

const TEACHER_OPTIONS: { value?: TeacherLink; label: string }[] = [
  { label: 'Any' },
  { value: 'with', label: 'Has a teacher' },
  { value: 'without', label: 'No teacher' },
];

/** Cuts both cohort grids. The teacher link is today's, not at signup. */
function SegmentFilter({ segment, onChange }: {
  segment: RetentionSegment;
  onChange: (s: RetentionSegment) => void;
}) {
  return (
    <div className="chart-card cohort-filter" style={{ marginBottom: 24 }}>
      <div className="cohort-filter-row">
        <span className="cohort-filter-label">Source</span>
        <div className="dashboard-time-toggle">
          {SOURCE_OPTIONS.map(o => (
            <button
              key={o.label}
              className={`time-toggle-btn ${segment.source === o.value ? 'active' : ''}`}
              title={o.hint}
              onClick={() => onChange({ ...segment, source: o.value })}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <div className="cohort-filter-row">
        <span className="cohort-filter-label">Teacher</span>
        <div className="dashboard-time-toggle">
          {TEACHER_OPTIONS.map(o => (
            <button
              key={o.label}
              className={`time-toggle-btn ${segment.teacher === o.value ? 'active' : ''}`}
              onClick={() => onChange({ ...segment, teacher: o.value })}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <p className="retention-note" style={{ margin: 0 }}>
        Filters the two cohort grids below; the charts above are always everyone. &ldquo;Has a
        teacher&rdquo; means enrolled with one now. How self-signups found us is only recorded
        from Sept 2026, so it is not a filter.
      </p>
    </div>
  );
}

/**
 * A cohort retention grid. The main Retention tab uses it for signup cohorts,
 * with a drill-down per cell; Teacher Stats for enrollment cohorts, without
 * one — the drill-down endpoint only knows signup cohorts.
 */
export function CohortGrid({ grain, rows: cohorts, drill, title, anchorLabel, note, controls }: {
  grain: Grain;
  rows: CohortRowView[];
  /** Cells of {COHORT_DRILLDOWN_MAX} or fewer list their users. Omit for none. */
  drill?: { includeAll?: boolean; segment?: RetentionSegment };
  title?: string;
  anchorLabel?: string;
  note?: ReactNode;
  /** Rendered under the note — a metric toggle, say. */
  controls?: ReactNode;
}) {
  const [openCell, setOpenCell] = useState<OpenCell | null>(null);
  const g = GRAIN[grain];
  const heading = title ?? `${g.title} Retention by Signup Cohort`;
  if (cohorts.length === 0) {
    return (
      <div className="chart-card">
        <h3>{heading}</h3>
        {controls}
        <p className="no-data">No cohorts in the {g.span}.</p>
      </div>
    );
  }

  const maxCols = Math.max(...cohorts.map(c => c.active.length));
  // Weighted retention across every cohort that has reached period n.
  const summary = Array.from({ length: maxCols }, (_, i) => {
    let active = 0;
    let size = 0;
    for (const c of cohorts) {
      if (i >= c.active.length) continue;
      active += c.active[i];
      size += c.size;
    }
    return { pct: size > 0 ? Math.round((active / size) * 100) : 0, size };
  });

  return (
    <div className="chart-card">
      <h3>{heading}</h3>
      <p className="retention-note">
        {note ?? (
          <>
            The {g.span}, whatever the range above. Each row is the users who signed up that {g.unit};{' '}
            {g.prefix}<em>n</em> is how many of them were active n {g.units} later ({g.prefix}0 = their
            signup {g.unit}). Click any cell of {COHORT_DRILLDOWN_MAX} or fewer to see who they are.
          </>
        )}
      </p>
      {controls}
      <div className="qs-table-wrap">
        <table className="cohort-grid">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>{anchorLabel ?? g.anchor}</th>
              <th>Users</th>
              {Array.from({ length: maxCols }, (_, i) => <th key={i}>{g.prefix}{i}</th>)}
            </tr>
          </thead>
          <tbody>
            {cohorts.map(row => (
              <tr key={row.start}>
                <td className="cohort-anchor">{g.label(row.start)}</td>
                <td className="cohort-size">{row.size}</td>
                {Array.from({ length: maxCols }, (_, i) => {
                  if (i >= row.active.length) return <td key={i} className="cohort-empty" />;
                  const count = row.active[i];
                  const pct = row.size > 0 ? Math.round((count / row.size) * 100) : 0;
                  // A cell is explorable only when there is someone to list and
                  // the list is short enough to be worth reading — the same
                  // ceiling the endpoint enforces.
                  const canDrill = drill !== undefined && count > 0 && count <= COHORT_DRILLDOWN_MAX;
                  const label = `${g.label(row.start)} · ${g.prefix}${i}`;
                  return (
                    <td
                      key={i}
                      className={canDrill ? 'cohort-cell-drillable' : undefined}
                      style={{ background: heatColor(pct), ...cellTextStyle(pct) }}
                      title={canDrill
                        ? `${count} of ${row.size} active — click to list them`
                        : `${count} of ${row.size} active`}
                      onClick={canDrill
                        ? () => setOpenCell({ grain, start: row.start, index: i, count, label })
                        : undefined}
                      role={canDrill ? 'button' : undefined}
                      tabIndex={canDrill ? 0 : undefined}
                      onKeyDown={canDrill
                        ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setOpenCell({ grain, start: row.start, index: i, count, label });
                          }
                        }
                        : undefined}
                    >
                      <span className="cohort-cell-pct">{pct}%</span>
                      <span className="cohort-cell-count">{count}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="cohort-summary">
              <td className="cohort-anchor">All cohorts</td>
              <td className="cohort-size">{cohorts.reduce((s, c) => s + c.size, 0)}</td>
              {summary.map((s, i) => (
                <td key={i} title={`${s.size} users have reached ${g.unit} ${i}`}>
                  <span className="cohort-cell-pct">{s.pct}%</span>
                  <span className="cohort-cell-count">n={s.size}</span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {openCell && (
        <CohortUsersModal cell={openCell} includeAll={drill?.includeAll} segment={drill?.segment} onClose={() => setOpenCell(null)} />
      )}
    </div>
  );
}

/**
 * The users behind one cell.
 *
 * Fetched on open rather than shipped with the grid: the grid is cohorts x
 * weeks, so inlining members would multiply its payload by the user base for a
 * panel opened one cell at a time.
 */
function CohortUsersModal({ cell, includeAll, segment, onClose }: {
  cell: OpenCell;
  includeAll?: boolean;
  segment?: RetentionSegment;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['cohortUsers', cell.grain, cell.start, cell.index, includeAll, segment],
    queryFn: () => adminApi.getCohortUsers(cell.start, cell.index, includeAll, cell.grain, segment),
  });

  return (
    <Modal isOpen onClose={onClose} title={`Active in ${cell.label}`} size="md">
      {isLoading && <div className="loading">Loading users…</div>}
      {error && <div className="error">Could not load the users for this cell.</div>}
      {data && (
        <>
          <p className="retention-note" style={{ marginTop: 0 }}>
            {data.users.length} of {data.cohortSize} in this cohort were active in{' '}
            {cell.grain === 'month' ? formatMonth(data.weekStart) : <>the week of {formatWeekRange(data.weekStart)}</>}.
          </p>
          {/* A count with no one behind it means the two disagree — say so
              rather than showing an empty list under a non-zero heading. */}
          {data.users.length === 0 ? (
            <p className="no-data">No users resolved for this cell.</p>
          ) : (
            <ul className="cohort-user-list">
              {data.users.map(u => (
                <li key={u.id}>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => navigate(`/users/${u.id}`)}
                  >
                    {u.name || u.id}
                  </button>
                  {u.email && <span className="cohort-user-email">{u.email}</span>}
                  <span className="cohort-user-signup">joined {u.signedUpOn}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Modal>
  );
}

/** Single-hue heatmap — 0% barely tinted, 100% solid. */
function heatColor(pct: number): string {
  const clamped = Math.max(0, Math.min(100, pct));
  return `rgba(99, 102, 241, ${(0.1 + (clamped / 100) * 0.75).toFixed(2)})`;
}

/** Once the heat fill is saturated enough, the theme's own text color loses
 *  contrast against it in light mode — flip to white there. */
function cellTextStyle(pct: number): { color?: string } {
  return pct >= 50 ? { color: '#fff' } : {};
}
